import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, StickyNote } from "lucide-react";
import { SlideMedia, marker } from "@/components/studio/SlideMedia";
import { animFor, transFor } from "@/lib/slideMotion";

// Read-only rendering of one slide, used by Present mode.
export function SlideView({ slide, config, playKey }) {
  const theme = config.themes.find((t) => t.id === slide.theme) || config.themes[0];
  const bg = (config.backgrounds || []).find((b) => b.id === slide.background);
  const fg = bg ? bg.fg : theme.fg;
  const bullets = (slide.bullets || []).filter((b) => (b || "").trim());
  const hasMedia = slide.image || slide.chart || slide.table || slide.video;
  const anim = animFor(slide.animation);
  const style = slide.bulletStyle || "dot";
  const body = (() => {
    if (slide.layout === "title") return (
      <div className="m-auto text-center w-full">
        <h1 className="text-5xl sm:text-6xl font-bold" style={{ color: fg }}>{slide.title || " "}</h1>
        {bullets[0] && <p className="text-2xl mt-4" style={{ color: theme.accent }}>{bullets[0]}</p>}
      </div>
    );
    if (slide.layout === "section") return (
      <div className="my-auto w-full pl-6 border-l-8" style={{ borderColor: theme.accent }}>
        <p className="text-sm uppercase tracking-widest opacity-60 mb-2">Section</p>
        <h1 className="text-5xl font-bold" style={{ color: fg }}>{slide.title}</h1>
        {bullets[0] && <p className="text-xl mt-3" style={{ color: theme.accent }}>{bullets[0]}</p>}
      </div>
    );
    if (slide.layout === "blank") return <div className="m-auto text-center"><h1 className="text-4xl font-bold" style={{ color: fg }}>{slide.title}</h1></div>;
    return (
      <>
        <h1 className="text-4xl sm:text-5xl font-bold mb-8" style={{ color: theme.accent }}>{slide.title}</h1>
        <div className={`flex-1 grid gap-8 min-h-0 ${slide.layout === "two-content" || hasMedia ? "sm:grid-cols-2" : "grid-cols-1"}`}>
          {slide.layout === "title-body" ? (
            <p className="text-2xl leading-relaxed" style={{ color: fg }}>{slide.body}</p>
          ) : (
            <ul className="space-y-3 text-2xl">
              {bullets.map((b, i) => <li key={i} className="flex gap-3"><span className="w-8 text-right shrink-0" style={{ color: theme.accent }}>{marker(style, i)}</span><span style={{ color: fg }}>{b}</span></li>)}
            </ul>
          )}
          {hasMedia && <div className="present-media"><SlideMedia slide={slide} theme={theme} fg={fg} updateSlide={() => {}} readOnly autoPlayVideo /></div>}
        </div>
      </>
    );
  })();
  return (
    <div className="w-full h-full p-12 sm:p-16 flex flex-col" style={{ background: bg ? bg.color : theme.bg, color: fg }}>
      <motion.div key={playKey} variants={anim} initial="hidden" animate="show" className="flex-1 flex flex-col min-h-0">{body}</motion.div>
    </div>
  );
}

export default function PresentMode({ doc, config, startAt = 0, onClose, onReachedEnd }) {
  const slides = doc.slides;
  const [i, setI] = useState(Math.min(startAt, slides.length - 1));
  const [notes, setNotes] = useState(false);
  const slide = slides[i];
  const trans = transFor(slide.transition);

  const go = useCallback((d) => setI((cur) => Math.max(0, Math.min(slides.length - 1, cur + d))), [slides.length]);
  useEffect(() => { if (i >= slides.length - 1) onReachedEnd?.(); }, [i, slides.length, onReachedEnd]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (["ArrowRight", " ", "PageDown", "Enter"].includes(e.key)) { e.preventDefault(); go(1); }
      else if (["ArrowLeft", "PageUp", "Backspace"].includes(e.key)) { e.preventDefault(); go(-1); }
      else if (e.key.toLowerCase() === "n") setNotes((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  return (
    <div className="fixed inset-0 z-[90] bg-black flex flex-col" data-testid="present-mode">
      <div className="flex-1 relative overflow-hidden" style={{ perspective: 1600 }} onClick={() => go(1)} data-testid="present-stage">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div key={slide.id} {...trans} className="absolute inset-0">
            <SlideView slide={slide} config={config} playKey={i} />
          </motion.div>
        </AnimatePresence>
      </div>
      {notes && (
        <div className="bg-[#0b1220] border-t border-white/10 px-6 py-3 text-slate-200 text-sm max-h-40 overflow-auto" data-testid="present-notes">
          <p className="text-[10px] uppercase tracking-widest text-[#22D3EE] mb-1">Speaker notes · slide {i + 1}</p>
          {slide.notes ? slide.notes : <span className="text-slate-500">No notes on this slide.</span>}
        </div>
      )}
      <div className="bg-[#070c1a] border-t border-white/10 px-4 py-2 flex items-center justify-between gap-3 text-slate-300 text-xs">
        <button data-testid="present-exit" onClick={onClose} className="inline-flex items-center gap-1.5 hover:text-white"><X className="w-4 h-4" /> Exit (Esc)</button>
        <div className="flex items-center gap-3">
          <button data-testid="present-prev" onClick={() => go(-1)} disabled={i === 0} className="p-1 rounded hover:bg-white/10 disabled:opacity-30"><ChevronLeft className="w-5 h-5" /></button>
          <span data-testid="present-counter" className="font-mono-data">{i + 1} / {slides.length}</span>
          <button data-testid="present-next" onClick={() => go(1)} disabled={i >= slides.length - 1} className="p-1 rounded hover:bg-white/10 disabled:opacity-30"><ChevronRight className="w-5 h-5" /></button>
        </div>
        <button data-testid="present-notes-toggle" onClick={() => setNotes((v) => !v)} className={`inline-flex items-center gap-1.5 hover:text-white ${notes ? "text-[#22D3EE]" : ""}`}><StickyNote className="w-4 h-4" /> Notes (N)</button>
      </div>
    </div>
  );
}
